import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AnnouncementModalComponent, AnnouncementModalData } from '../../components/announcement-modal/announcement-modal.component';
import { AnnouncementsApiService, LastAnnouncementResponse, Announcement } from './api/announcements-api.service';
import { catchError, firstValueFrom, of } from 'rxjs';
import { captureException } from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class AnnouncementsService {
  private dialog = inject(MatDialog);
  private announcementsApiService = inject(AnnouncementsApiService);
  private shownAnnouncementIds = new Set<string>(); // Защита от повторного показа в рамках сессии

  /**
   * Проверить и показать последний анонс, если он есть
   * Вызывается при инициализации кабинета
   */
  async checkAndShowLastAnnouncement(): Promise<void> {
    try {
      console.log('[Announcements] Checking for last announcement...');
      const response: LastAnnouncementResponse = await firstValueFrom(
        this.announcementsApiService.getLastAnnouncement().pipe(
          catchError((error) => {
            console.error('[Announcements] Failed to fetch last announcement:', error);
            captureException(error);
            return of(null); // В случае ошибки не показываем модалку
          })
        )
      );

      console.log('[Announcements] Response:', response);

      // Обрабатываем разные форматы ответа от бэкенда
      let announcement: Announcement | null = null;

      if (!response) {
        console.log('[Announcements] No announcement to show (null response)');
        return;
      }

      if (typeof response === 'object') {
        // Если ответ в формате {show: false}
        if ('show' in response && (response as { show: boolean }).show === false) {
          console.log('[Announcements] No announcement to show (show: false)');
          return;
        }
        // Если ответ в формате {show: true, announcement: {...}}
        if ('show' in response && 'announcement' in response) {
          const wrappedResponse = response as { show: boolean; announcement?: Announcement };
          if (wrappedResponse.show && wrappedResponse.announcement) {
            const ann = wrappedResponse.announcement;
            if (ann.id && ann.content) {
              announcement = ann;
            }
          }
        }
        // Если ответ - объект анонса напрямую (с полем id)
        else if ('id' in response && (response as Announcement).id) {
          announcement = response as Announcement;
        }
      }

      if (announcement && announcement.id) {
        // Проверяем, не показывали ли уже этот анонс в текущей сессии
        if (!this.shownAnnouncementIds.has(announcement.id)) {
          console.log('[Announcements] Showing announcement:', announcement.id);
          this.showAnnouncement(announcement);
          this.shownAnnouncementIds.add(announcement.id);
        } else {
          console.log('[Announcements] Announcement already shown in this session:', announcement.id);
        }
      } else {
        console.log('[Announcements] No announcement to show');
      }
    } catch (error) {
      // Ошибка уже обработана в catchError выше
      console.error('[Announcements] Error in checkAndShowLastAnnouncement:', error);
    }
  }

  /**
   * Показать модальное окно с анонсом
   */
  private showAnnouncement(announcement: Announcement): void {
    // Проверяем, не открыта ли уже модалка
    const existingDialog = this.dialog.openDialogs.find(
      (d) => d.componentInstance instanceof AnnouncementModalComponent
    );

    if (existingDialog) {
      return; // Модалка уже открыта
    }

    const dialogRef = this.dialog.open(AnnouncementModalComponent, {
      data: { announcement } as AnnouncementModalData,
      panelClass: 'announcement-modal',
      disableClose: false, // Разрешаем закрытие по клику на backdrop
      autoFocus: false,
      restoreFocus: true,
      maxWidth: '100vw',
      width: 'auto',
    });

    // После закрытия модалки можно выполнить дополнительные действия, если нужно
    dialogRef.afterClosed().subscribe(() => {
      // Модалка закрыта
    });
  }

  /**
   * Очистить кэш показанных анонсов (например, при выходе пользователя)
   */
  clearShownAnnouncements(): void {
    this.shownAnnouncementIds.clear();
  }
}
